/**
 * INT-0009 — the ONE appearance editor, shared by the admin Appearance page
 * and the org-creation design step (owner 2026-09-07: "make the components
 * as sharable as possible"). A hook rather than a component so each page
 * keeps layout freedom: it returns the controls column, the browser-framed
 * live preview, and the save/dirty state — the pages decide where the grid,
 * header, and CTA live.
 *
 * Structure follows the templates, not the legacy banner flow:
 * - premier → dark hero picker (sport set when curated + 3 generic)
 * - classic → CSS hero treatments (Clean / Tint / Lines)
 * - simple  → the pre-existing Customize Banner editor (banner REQUIRED,
 *   Hero default — the visibility toggle is retired, owner 2026-09-09)
 * Palette options are COMPUTED per template (shared paletteWorksOn) and the
 * font picker offers only the template's complementary set. Every control
 * updates the preview iframe via the public site's preview params
 * (?template=&palette=&font=&hero=) — nothing persists until Save, which
 * writes organization.settings through the normal update path.
 */
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Monitor, Smartphone, ChevronDown } from 'lucide-react';
import { useAuth } from '@/auth/clerkShim';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import SitePreviewFrame from './SitePreviewFrame';
import BannerEditor from '@/components/BannerEditor/BannerEditor';
import LogoEditor from '@/components/LogoEditor/LogoEditor';
import { ListSkeleton } from '@/components/Page/PageSkeletons';
import useOrganizationStore from '@store/organizationStore';
import useAdminStore from '@store/adminStore';
import { publicSiteUrl } from '@/utils/publicSiteUrl';
import { buildSiteDesignSettings, buildLogoSettings } from './appearanceSettings';
import { SPORT_COLOR_PALETTES } from '@staty/shared/banners';
import {
  TEMPLATE_FONTS,
  BODY_FONTS,
  DEFAULT_BODY_FONT_KEY,
  CUSTOM_PALETTE_KEY,
  normalizeHex,
  normalizeCustomColors,
  customColorsParam,
  resolveOrgPalette,
  CLASSIC_HERO_TREATMENTS,
  premierHeroesForSport,
  paletteWorksOn,
  effectivePremierInk,
  classicHeroCss,
} from '@staty/shared/orgAppearance';

type AnyDoc = Record<string, any>;
type HomeTemplate = 'classic' | 'premier' | 'simple';
type AppearanceSection = 'site-design' | 'logo' | 'banner';


const TEMPLATES: Array<{ id: HomeTemplate; name: string; blurb: string }> = [
  { id: 'classic', name: 'Classic', blurb: 'Light and structured: logo-led, calm.' },
  { id: 'premier', name: 'Premier', blurb: 'Bold and dark: full-bleed hero in your colors.' },
  // Display name only (owner 2026-09-09) — the stored key stays 'simple'.
  { id: 'simple', name: 'Essentials', blurb: 'Already have a website? A no-frills home for your schedules, registration, and scores.' },
];

export interface AppearanceEditorOptions {
  league: AnyDoc | null;
  admin: AnyDoc | null;
  /** Called after a successful save (the create flow navigates onward). */
  onSaved?: () => void;
  /**
   * Template shown when the org has NO saved homeTemplate (owner
   * 2026-09-10: the create flow starts new orgs on 'premier'). An
   * explicitly saved choice — 'simple' included — always wins over this.
   * Existing-org surfaces (/admin/appearance) omit it: absent = 'simple'
   * (owner 2026-09-08).
   */
  defaultTemplate?: HomeTemplate;
  /**
   * 'stacked' (default) = one flat card column with eyebrow labels and the
   * aggregate save — the create-flow design step. 'tabbed' (owner
   * 2026-09-25, /admin/appearance) = exclusive Site design / Logo / Banner
   * tabs, each section with its OWN save button.
   */
  layout?: 'stacked' | 'tabbed';
  /**
   * INT-0028 (create-flow design step): when the org has no saved palette,
   * pre-select the first compatible one so Continue is one click and the
   * preview shows real colors on arrival. /admin/appearance omits it.
   */
  preselectPalette?: boolean;
  /**
   * INT-0028 (create-flow design step, stacked layout only): the headline
   * font card and the Logo section (incl. the AI generator) sit behind a
   * CLOSED "Advanced" disclosure, so the step is template + colors.
   */
  advancedCollapsed?: boolean;
}

export default function useAppearanceEditor({ league, admin, onSaved, defaultTemplate, layout = 'stacked', preselectPalette = false, advancedCollapsed = false }: AppearanceEditorOptions) {
  const { getToken } = useAuth();

  const saved = (league?.settings || {}) as AnyDoc;
  const resolveTemplateDraft = (v: unknown): HomeTemplate =>
    v === 'premier' || v === 'classic' || v === 'simple' ? v : (defaultTemplate || 'simple');
  const sport: string = league?.sports?.[0] || 'basketball';
  const palettes: Record<string, AnyDoc> =
    (SPORT_COLOR_PALETTES as AnyDoc)[sport] || (SPORT_COLOR_PALETTES as AnyDoc).basketball;

  // Draft state — params-only until Save (grilling #7).
  const [template, setTemplate] = useState<HomeTemplate>(
    // Absent = 'simple' (owner 2026-09-08) unless the surface passes a
    // defaultTemplate (create flow: 'premier') — the picker shows the truth
    // for saved orgs either way.
    resolveTemplateDraft(saved.homeTemplate),
  );
  const [paletteKey, setPaletteKey] = useState<string>(saved.banner?.colorPaletteKey || '');
  // INT-0057: the organizer's own pair, used when paletteKey is 'custom'.
  // Raw strings while typing; `validCustom` is the normalized pair or null.
  // Seeded from the saved pair, else the saved/sport-default palette.
  const [customColors, setCustomColors] = useState<{ primary: string; secondary: string }>(() => {
    const savedPair = normalizeCustomColors(saved.banner?.customColors);
    if (savedPair) return savedPair;
    const r = resolveOrgPalette(sport, saved.banner);
    return { primary: r.primary, secondary: r.secondary };
  });
  const validCustom = normalizeCustomColors(customColors);
  const customInvalid = paletteKey === CUSTOM_PALETTE_KEY && !validCustom;
  const customParam = validCustom ? customColorsParam(validCustom) : '';
  const [heroPremier, setHeroPremier] = useState<string>(saved.heroPremier || '');
  const [heroClassic, setHeroClassic] = useState<string>(saved.heroClassic || 'clean');
  const [fontPremier, setFontPremier] = useState<string>(saved.fonts?.premier || 'oswald');
  const [fontClassic, setFontClassic] = useState<string>(saved.fonts?.classic || 'inter');
  // INT-0056: body font, every template (owner 2026-10-30). Absent = Inter.
  const [fontBody, setFontBody] = useState<string>(saved.fonts?.body || DEFAULT_BODY_FONT_KEY);
  // Hero logo emphasis (owner 2026-09-09): 'prominent' = big logo beside
  // the name, right rail drops to a bottom row. Absent saved = standard.
  const [heroLogo, setHeroLogo] = useState<'standard' | 'prominent'>(saved.heroLogo === 'prominent' ? 'prominent' : 'standard');
  // INT-0013: ONE focal point for the custom hero photo (percent coords).
  const [heroFocal, setHeroFocal] = useState<{ x: number; y: number }>(saved.heroFocal || { x: 50, y: 50 });
  const [heroUploading, setHeroUploading] = useState(false);
  const postLeagueHero = useAdminStore((state: any) => state.postLeagueHero);
  const [mobilePreview, setMobilePreview] = useState(false);
  // Tabbed layout: which subsection is visible. The Banner tab exists only
  // on Essentials — leaving 'simple' while it's active falls back safely.
  const [activeSection, setActiveSection] = useState<AppearanceSection>('site-design');
  useEffect(() => {
    if (activeSection === 'banner' && template !== 'simple') setActiveSection('site-design');
  }, [activeSection, template]);
  // The browser-framed iframe (1280px desktop scaled / 390px mobile) lives
  // in SitePreviewFrame (INT-0028 extraction) — see that file for the sizing
  // rationale.
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  // Dirty tracking (owner 2026-09-07; split per SECTION 2026-09-25): a Save
  // stays disabled until its own section differs from the last-persisted
  // state; a successful save re-baselines that section's snapshot.
  // customColors only counts toward the draft while Custom is selected, so
  // the seeded pair never makes a freshly loaded page dirty.
  const siteDraft = { template, paletteKey, heroPremier, heroClassic, fontPremier, fontClassic, fontBody, heroFocal, customColors: paletteKey === CUSTOM_PALETTE_KEY ? validCustom : null };
  const [siteSnapshot, setSiteSnapshot] = useState(() => JSON.stringify(siteDraft));
  const [logoSnapshot, setLogoSnapshot] = useState<string>(heroLogo);
  /** Picked-but-unsaved AI logo variant → `logo_image` preview param.
   * Counts as DIRTY (owner 2026-09-09); the Logo save commits it. */
  const [logoDraft, setLogoDraft] = useState<{ imageUrl: string } | null>(null);
  const siteDirty = JSON.stringify(siteDraft) !== siteSnapshot;
  const logoDirty = heroLogo !== logoSnapshot || Boolean(logoDraft);
  const isDirty = siteDirty || logoDirty;
  const selectLogoVariant = useAdminStore((state: any) => state.selectLogoVariant);
  // Bumped after Save commits a pending variant so LogoEditor clears its
  // own selection state.
  const [logoSaveSignal, setLogoSaveSignal] = useState(0);
  // The banner editor's UNSAVED custom-build selection — folded into the
  // simple template's preview URL so a selected banner shows before Save
  // (owner 2026-09-07).
  // Sticky Banner-header save (owner 2026-09-25): bump → BannerEditor saves
  // itself; saveable state drives the button.
  const [bannerSaveSignal, setBannerSaveSignal] = useState(0);
  const [bannerSaveable, setBannerSaveable] = useState({ canSave: false, busy: false });
  const [bannerDraft, setBannerDraft] = useState<
    | { templateKey: string; colorKey: string; fontKey: string; logoPosition: 'left' | 'right' }
    | { imageUrl: string }
    | null
  >(null);

  // The create flow (and org switches) load `league` AFTER first render —
  // resync the draft from the newly-arrived saved settings and re-baseline
  // the dirty snapshot.
  const leagueId = league?._id;
  useEffect(() => {
    if (!leagueId) return;
    const sv = (league?.settings || {}) as AnyDoc;
    const next = {
      template: resolveTemplateDraft(sv.homeTemplate),
      paletteKey: sv.banner?.colorPaletteKey || '',
      heroPremier: sv.heroPremier || '',
      heroClassic: sv.heroClassic || 'clean',
      fontPremier: sv.fonts?.premier || 'oswald',
      fontClassic: sv.fonts?.classic || 'inter',
      fontBody: sv.fonts?.body || DEFAULT_BODY_FONT_KEY,
      heroLogo: (sv.heroLogo === 'prominent' ? 'prominent' : 'standard') as 'standard' | 'prominent',
      heroFocal: sv.heroFocal || { x: 50, y: 50 },
      // KEY ORDER matters: the dirty check compares JSON strings against
      // siteDraft, so this object must list keys in siteDraft's order.
      customColors: sv.banner?.colorPaletteKey === CUSTOM_PALETTE_KEY ? normalizeCustomColors(sv.banner?.customColors) : null,
    };
    setTemplate(next.template);
    setPaletteKey(next.paletteKey);
    const savedPair = normalizeCustomColors(sv.banner?.customColors);
    if (savedPair) setCustomColors(savedPair);
    setHeroPremier(next.heroPremier);
    setHeroClassic(next.heroClassic);
    setFontPremier(next.fontPremier);
    setFontClassic(next.fontClassic);
    setFontBody(next.fontBody);
    setHeroLogo(next.heroLogo);
    setHeroFocal(next.heroFocal);
    const { heroLogo: nextHeroLogo, ...nextSite } = next;
    setSiteSnapshot(JSON.stringify(nextSite));
    setLogoSnapshot(nextHeroLogo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leagueId]);

  const compatiblePalettes = useMemo(() => {
    const compat = Object.entries(palettes).filter(([, p]) => paletteWorksOn(template, p as { primary: string; secondary: string }));
    // Owner 2026-09-08: each template offers SIX palettes (data guarantees
    // ≥6 compatible per sport per template). Keep a saved palette visible
    // even when it falls outside the first six.
    const shown = compat.slice(0, 6);
    if (paletteKey && !shown.some(([k]) => k === paletteKey)) {
      const saved = compat.find(([k]) => k === paletteKey);
      if (saved) shown.splice(shown.length - 1, 1, saved);
    }
    return shown;
  }, [palettes, template, paletteKey]);
  // INT-0028: one-click design step — a new org arrives with no saved
  // palette, so pick the first compatible one. Re-runs after the
  // league-arrival resync above resets paletteKey to ''.
  useEffect(() => {
    if (!preselectPalette || paletteKey || !compatiblePalettes.length) return;
    setPaletteKey(compatiblePalettes[0][0]);
  }, [preselectPalette, paletteKey, compatiblePalettes]);
  const heroOptions = useMemo(() => premierHeroesForSport(sport), [sport]);
  const fontOptions = template === 'simple' ? [] : TEMPLATE_FONTS[template];
  const activeFontKey = template === 'premier' ? fontPremier : fontClassic;

  const previewSrc = useMemo(() => {
    if (!league?.name) return '';
    const params = new URLSearchParams({ template });
    if (paletteKey === CUSTOM_PALETTE_KEY) {
      // INT-0057: preview the custom pair; an invalid pair previews nothing new.
      if (customParam) { params.set('palette', CUSTOM_PALETTE_KEY); params.set('colors', customParam); }
    } else if (paletteKey) {
      params.set('palette', paletteKey);
    }
    // The logo renders on every template (SubHeader + hero), so its unsaved
    // AI-variant preview applies regardless of the selected template.
    if (logoDraft?.imageUrl) params.set('logo_image', logoDraft.imageUrl);
    // INT-0056: the body font applies on every template.
    params.set('body_font', fontBody);
    if (template === 'premier') {
      if (heroPremier) params.set('hero', heroPremier);
      params.set('font', fontPremier);
      params.set('hero_logo', heroLogo);
      if (heroPremier === 'custom') params.set('hero_focal', `${Math.round(heroFocal.x)},${Math.round(heroFocal.y)}`);
    } else if (template === 'classic') {
      params.set('hero', heroClassic);
      params.set('font', fontClassic);
      params.set('hero_logo', heroLogo);
    } else if (template === 'simple') {
      // Draft overrides for the simple preview (never persisted): the
      // unsaved visibility toggle, a custom-build draft (legacy
      // LeagueTournamentLayout contract), or the picked AI variant's image.
      if (bannerDraft && 'imageUrl' in bannerDraft) {
        params.set('banner_image', bannerDraft.imageUrl);
      } else if (bannerDraft) {
        params.set('sports_color', `${sport}_${bannerDraft.colorKey}`);
        params.set('banner_template', bannerDraft.templateKey);
        if (bannerDraft.fontKey) params.set('banner_font', bannerDraft.fontKey);
        params.set('banner_logo', bannerDraft.logoPosition);
      }
    }
    return publicSiteUrl(league.name, `/home?${params.toString()}`);
  }, [league?.name, template, paletteKey, customParam, heroPremier, heroClassic, fontPremier, fontClassic, fontBody, heroLogo, heroFocal, bannerDraft, sport, logoDraft]);

  // Commit a picked-but-unsaved AI logo variant (owner 2026-09-09). Shared
  // by the aggregate save (stacked) and the Logo section save (tabbed).
  const commitLogoVariant = async () => {
    if (!logoDraft?.imageUrl || !league?._id) return;
    const token = await getToken();
    await selectLogoVariant(league._id, logoDraft.imageUrl, token);
    refreshOrg({ ...league, hasLogo: true, logoExt: 'png', updatedAt: new Date().toISOString() });
    setLogoDraft(null);
    setLogoSaveSignal((n) => n + 1);
  };

  // PUT a settings object and merge the SERVER result back into the store —
  // `saved` must never go stale, or the next section's spread clobbers a
  // just-saved sub-object (the old single-save flow had exactly that latent
  // bug with BannerEditor's writes; owner 2026-09-25 split-save fix).
  const putSettings = async (settings: AnyDoc) => {
    const updateOrganization = (useOrganizationStore.getState() as AnyDoc).updateOrganization(getToken);
    const res = await updateOrganization({ _id: league!._id, settings });
    // updateOrganization swallows axios errors and returns undefined — treat
    // that as failure so callers surface the toast.
    if (!res?.result) throw new Error('settings save failed');
    refreshOrg(res.result);
    return res.result as AnyDoc;
  };

  /** Aggregate save — the stacked (create-flow) path: everything at once. */
  const save = async () => {
    if (!league?._id) return;
    setSaving(true);
    try {
      await commitLogoVariant();
      await putSettings({ ...buildSiteDesignSettings(saved, siteDraft), heroLogo });
      setSiteSnapshot(JSON.stringify(siteDraft));
      setLogoSnapshot(heroLogo);
      toast.success('Appearance saved');
      onSaved?.();
    } catch {
      toast.error("Couldn't save appearance. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  /** Section saves (tabbed layout) — each persists ONLY its own fields. */
  const saveSiteDesign = async () => {
    if (!league?._id) return;
    setSaving(true);
    try {
      await putSettings(buildSiteDesignSettings(saved, siteDraft));
      setSiteSnapshot(JSON.stringify(siteDraft));
      toast.success('Site design saved');
      onSaved?.();
    } catch {
      toast.error("Couldn't save site design. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const saveLogo = async () => {
    if (!league?._id) return;
    setSaving(true);
    try {
      await commitLogoVariant();
      await putSettings(buildLogoSettings(saved, { heroLogo }));
      setLogoSnapshot(heroLogo);
      toast.success('Logo saved');
      onSaved?.();
    } catch {
      toast.error("Couldn't save logo. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  // Push a saved org (logo upload/select result) back into the store so the
  // outlet-context `league` refreshes — the old inline uploader never did,
  // leaving hasLogo stale until a full refetch.
  const refreshOrg = (savedOrg: AnyDoc | null) => {
    if (!savedOrg?._id) return;
    const store = useOrganizationStore.getState() as AnyDoc;
    store.setOrganizations(
      (store.organizations || []).map((o: AnyDoc) => (o._id === savedOrg._id ? { ...o, ...savedOrg } : o)),
    );
  };

  const swatchBtn = (selected: boolean) =>
    `rounded-lg border p-3 text-left transition-colors cursor-pointer ${
      selected ? 'border-primary ring-1 ring-primary bg-primary/5' : 'border-border hover:border-muted-foreground/40'
    }`;

  /** Subsection eyebrow between control groups (owner 2026-09-24: the
      borderless cards read as ONE section — Template and Logo were being
      confused as parts of the same thing). Rendered on both consumers
      (Appearance page + create-flow design step) since the hook owns it. */
  const sectionLabel = (text: string) => (
    <div className="flex items-center gap-3 pt-2 first:pt-0" data-testid={`appearance-section-${text.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
      <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{text}</span>
      <div className="h-px flex-1 bg-border" aria-hidden />
    </div>
  );

  /** The stacked control cards — the page owns the wrapping grid column. */
  // ── Section card chunks — composed differently per layout ──────────────
  const coreDesignCards = (
    <>

          <Card className="shadow-sm border-0" data-testid="appearance-template">
            <CardHeader>
              <CardTitle>Template</CardTitle>
              <CardDescription>How your public homepage is composed.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className={mobilePreview ? 'grid gap-3 sm:grid-cols-3' : 'grid gap-3 grid-cols-1'} role="radiogroup" aria-label="Homepage template">
                {TEMPLATES.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    role="radio"
                    aria-checked={template === t.id}
                    onClick={() => setTemplate(t.id)}
                    className={swatchBtn(template === t.id)}
                  >
                    <span className="font-medium">{t.name}</span>
                    <p className="mt-1 text-sm text-muted-foreground">{t.blurb}</p>
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>

          {template === 'premier' && (
            <Card className="shadow-sm border-0" data-testid="appearance-hero-premier">
              <CardHeader>
                <CardTitle>Hero background</CardTitle>
                <CardDescription>
                  A dark backdrop behind your hero. Your colors render on top of it.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className={mobilePreview ? 'grid gap-3 sm:grid-cols-3' : 'grid gap-3 grid-cols-1'}>
                  {heroOptions.map((h) => (
                    <button
                      key={h.key}
                      type="button"
                      onClick={() => setHeroPremier(h.key === heroPremier ? '' : h.key)}
                      className={swatchBtn(heroPremier === h.key)}
                    >
                      <div
                        className="h-16 w-full rounded-md mb-2"
                        style={{ background: h.background, backgroundSize: 'cover', backgroundPosition: 'center' }}
                        aria-hidden="true"
                      />
                      <span className="text-sm font-medium">{h.name}</span>
                      {h.sport !== 'generic' && (
                        <span className="ml-1 text-xs text-muted-foreground capitalize">· {h.sport}</span>
                      )}
                    </button>
                  ))}
                </div>
                {/* INT-0013: the org's OWN hero photo + click-to-set focal. */}
                <div className="mt-4 border-t border-border pt-4" data-testid="appearance-hero-custom">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="text-sm font-medium">Your photo</p>
                      <p className="text-xs text-muted-foreground">Upload your own hero image. Landscape photos work best.</p>
                    </div>
                    <label className="inline-flex cursor-pointer items-center rounded-md border border-border px-3 py-2 text-sm font-medium hover:bg-muted">
                      {heroUploading ? 'Uploading…' : league?.hasHeroImage ? 'Replace photo' : 'Upload photo'}
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={async (e) => {
                          const f = e.target.files?.[0];
                          e.target.value = '';
                          if (!f || !league?.name || !admin?._id) return;
                          setHeroUploading(true);
                          const token = await getToken();
                          postLeagueHero(f, league.name, admin._id, token, (result: AnyDoc | null) => {
                            setHeroUploading(false);
                            if (result) {
                              refreshOrg(result);
                              setHeroPremier('custom');
                            }
                          });
                        }}
                      />
                    </label>
                  </div>
                  {league?.hasHeroImage && (
                    <button
                      type="button"
                      onClick={() => setHeroPremier(heroPremier === 'custom' ? '' : 'custom')}
                      className={`mt-3 block w-full text-left ${swatchBtn(heroPremier === 'custom')}`}
                    >
                      <span className="text-sm font-medium">Use my photo</span>
                      <p className="text-xs text-muted-foreground">Cropped around your focal point on every screen size.</p>
                    </button>
                  )}
                  {league?.hasHeroImage && heroPremier === 'custom' && (
                    <div className="mt-3">
                      <p className="mb-1 text-xs text-muted-foreground">
                        Click the important part of the photo. The crop keeps it in view on every screen.
                      </p>
                      <div
                        className="relative cursor-crosshair overflow-hidden rounded-lg border border-border"
                        onClick={(e) => {
                          const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                          setHeroFocal({
                            x: Math.round(((e.clientX - rect.left) / rect.width) * 100),
                            y: Math.round(((e.clientY - rect.top) / rect.height) * 100),
                          });
                        }}
                        role="button"
                        aria-label="Set hero focal point"
                        data-testid="hero-focal-editor"
                      >
                        <img
                          src={`https://staty-bucket.sfo2.cdn.digitaloceanspaces.com/leagues/${String(league.name || '').toLowerCase()}/heropremier.${league.heroImageExt}?v=${Date.parse(league.updatedAt) || 0}`}
                          alt="Hero"
                          className="block w-full"
                        />
                        <span
                          className="pointer-events-none absolute h-6 w-6 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white shadow-[0_0_0_2px_rgba(0,0,0,0.5)]"
                          style={{ left: `${heroFocal.x}%`, top: `${heroFocal.y}%` }}
                          aria-hidden="true"
                        />
                      </div>
                    </div>
                  )}
                </div>
                <p className="mt-2 text-xs text-muted-foreground">
                  Click a selected option again to clear it (plain ink gradient).
                </p>
              </CardContent>
            </Card>
          )}

          {template === 'classic' && (
            <Card className="shadow-sm border-0" data-testid="appearance-hero-classic">
              <CardHeader>
                <CardTitle>Hero treatment</CardTitle>
                <CardDescription>The identity band sits on a white page. Pick its backdrop.</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid gap-3 sm:grid-cols-3">
                  {CLASSIC_HERO_TREATMENTS.map((h) => {
                    const pal = paletteKey === CUSTOM_PALETTE_KEY ? validCustom : (paletteKey && palettes[paletteKey] ? palettes[paletteKey] : null);
                    // Same CSS the public hero renders — one source, no drift.
                    const previewCss = classicHeroCss(h.key, pal?.primary || '#5267A3', pal?.secondary);
                    return (
                      <button
                        key={h.key}
                        type="button"
                        onClick={() => setHeroClassic(h.key)}
                        className={swatchBtn(heroClassic === h.key)}
                      >
                        <div className="h-16 w-full rounded-md mb-2 border border-gray-200" style={previewCss || { background: '#ffffff' }} aria-hidden="true" />
                        <span className="text-sm font-medium">{h.name}</span>
                        <p className="text-xs text-muted-foreground">{h.blurb}</p>
                      </button>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Shown on EVERY template incl. Essentials (owner 2026-09-24).
              History: simple hid this card (owner 2026-09-09) when it truly
              was a dead control — but the ADR 0061 theming vars since made
              the palette drive simple too (nav Sign Up, Register CTA,
              inner-page accents via --org-primary; verified live on the
              valley demo). Banner colors remain their own pick INSIDE
              Customize banner. */}
          <Card className="shadow-sm border-0" data-testid="appearance-palette">
            <CardHeader>
              <CardTitle>Color palette</CardTitle>
              <CardDescription>
                {template === 'simple'
                  ? 'Accent color for buttons and links across your whole public site. Your banner colors are chosen separately below.'
                  : `Used across your whole public site. Only colors that suit the ${template} template are shown.`}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className={mobilePreview ? 'grid gap-3 sm:grid-cols-3' : 'grid gap-3 grid-cols-1'}>
                {compatiblePalettes.map(([key, p]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setPaletteKey(key)}
                    className={swatchBtn(paletteKey === key)}
                  >
                    <div className="flex gap-1.5 mb-2" aria-hidden="true">
                      <span className="h-8 w-1/2 rounded-md" style={{ backgroundColor: (p as AnyDoc).primary }} />
                      {/* Premier discards light secondaries for the dark ink
                          (effectivePremierInk) — show what will actually render. */}
                      <span
                        className="h-8 w-1/2 rounded-md border border-gray-200"
                        style={{
                          backgroundColor: template === 'premier'
                            ? effectivePremierInk((p as AnyDoc).secondary)
                            : (p as AnyDoc).secondary,
                        }}
                      />
                    </div>
                    <span className="text-sm font-medium">{(p as AnyDoc).name || key.replace(/_/g, ' ')}</span>
                  </button>
                ))}
                {/* INT-0057 (owner 2026-10-30): the organizer's own pair. Picking
                    the tile seeds the fields from the palette that was selected. */}
                <button
                  type="button"
                  onClick={() => {
                    if (paletteKey !== CUSTOM_PALETTE_KEY) {
                      const p = palettes[paletteKey] as AnyDoc | undefined;
                      if (p) setCustomColors({ primary: p.primary, secondary: p.secondary });
                    }
                    setPaletteKey(CUSTOM_PALETTE_KEY);
                  }}
                  className={swatchBtn(paletteKey === CUSTOM_PALETTE_KEY)}
                  data-tour="appearance-palette-custom"
                  data-testid="appearance-palette-custom"
                >
                  <div className="flex gap-1.5 mb-2" aria-hidden="true">
                    <span className="h-8 w-1/2 rounded-md border border-gray-200" style={{ backgroundColor: validCustom?.primary || '#e5e7eb' }} />
                    <span
                      className="h-8 w-1/2 rounded-md border border-gray-200"
                      style={{ backgroundColor: validCustom ? (template === 'premier' ? effectivePremierInk(validCustom.secondary) : validCustom.secondary) : '#e5e7eb' }}
                    />
                  </div>
                  <span className="text-sm font-medium">Custom</span>
                  <p className="text-xs text-muted-foreground">Your own two colors</p>
                </button>
              </div>
              {paletteKey === CUSTOM_PALETTE_KEY && (
                <div className="mt-4 space-y-3" data-testid="appearance-custom-colors">
                  <div className="grid gap-3 sm:grid-cols-2">
                    {([
                      { key: 'primary', label: 'Primary', hint: 'Buttons, links and accents.' },
                      { key: 'secondary', label: 'Secondary', hint: template === 'premier' ? 'Hero and footer ink. A light color falls back to a dark ink.' : 'Heading ink and secondary accents.' },
                    ] as const).map((field) => {
                      const raw = customColors[field.key];
                      const valid = normalizeHex(raw);
                      return (
                        <label key={field.key} className="block">
                          <span className="mb-1.5 block text-sm font-medium">{field.label}</span>
                          <div className="flex items-center gap-2 rounded-xl bg-muted/50 px-3 py-2 ring-1 ring-border">
                            <input
                              type="color"
                              aria-label={`${field.label} color`}
                              value={valid ? valid.toLowerCase() : '#000000'}
                              onChange={(e) => setCustomColors((c) => ({ ...c, [field.key]: e.target.value.toUpperCase() }))}
                              className="h-8 w-10 cursor-pointer rounded border-0 bg-transparent p-0"
                              data-testid={`appearance-custom-${field.key}`}
                            />
                            <input
                              type="text"
                              aria-label={`${field.label} hex`}
                              value={raw}
                              onChange={(e) => setCustomColors((c) => ({ ...c, [field.key]: e.target.value.replace(/[^#0-9a-fA-F]/g, '').slice(0, 7) }))}
                              placeholder="#1D4ED8"
                              className="w-full bg-transparent font-mono text-sm outline-none"
                            />
                          </div>
                          <span className="mt-1 block text-xs text-muted-foreground">{field.hint}</span>
                        </label>
                      );
                    })}
                  </div>
                  {!validCustom ? (
                    <p className="text-xs text-destructive">Enter both colors as 6-digit hex values, for example #1D4ED8.</p>
                  ) : !paletteWorksOn(template, validCustom) ? (
                    <p className="text-xs text-amber-700" data-testid="appearance-custom-contrast">
                      Heads up: this primary is hard to read {template === 'classic' ? 'on the white Classic page' : 'against the Premier hero ink'}. The preview shows the result; you can still save.
                    </p>
                  ) : null}
                </div>
              )}
            </CardContent>
          </Card>

    </>
  );

  const fontCard = (
    <>
          {fontOptions.length > 0 && (
            <Card className="shadow-sm border-0" data-testid="appearance-font">
              <CardHeader>
                <CardTitle>Headline font</CardTitle>
                <CardDescription>Faces that complement the {template} template.</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid gap-3 sm:grid-cols-3">
                  {fontOptions.map((f) => (
                    <button
                      key={f.key}
                      type="button"
                      onClick={() => (template === 'premier' ? setFontPremier(f.key) : setFontClassic(f.key))}
                      className={swatchBtn(activeFontKey === f.key)}
                    >
                      <span className="block text-2xl leading-tight" style={{ fontFamily: f.stack }}>
                        Game night
                      </span>
                      <span className="text-xs text-muted-foreground">{f.name}</span>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* INT-0056 (owner 2026-10-30): the site's text face, one choice for
              every template. Each option renders in its own family (the
              admin index.html font link loads them). */}
          <Card className="shadow-sm border-0" data-testid="appearance-body-font">
            <CardHeader>
              <CardTitle>Body font</CardTitle>
              <CardDescription>
                The face for navigation, tables, posts and registration forms{template === 'simple' ? ', and for headings on the Simple template' : ''}.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid gap-3 sm:grid-cols-2" role="radiogroup" aria-label="Body font">
                {BODY_FONTS.map((f) => (
                  <button
                    key={f.key}
                    type="button"
                    role="radio"
                    aria-checked={fontBody === f.key}
                    onClick={() => setFontBody(f.key)}
                    className={swatchBtn(fontBody === f.key)}
                    data-tour={`appearance-body-font-${f.key}`}
                  >
                    {/* Owner 2026-10-30: the NAME set in its own face IS the
                        preview (Google Fonts idiom); no sample line. */}
                    <span className="flex items-baseline justify-between gap-2">
                      <span className="block text-2xl font-semibold leading-tight" style={{ fontFamily: f.stack }}>{f.name}</span>
                      {f.key === DEFAULT_BODY_FONT_KEY && (
                        <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">Default</span>
                      )}
                    </span>
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>

    </>
  );

  const siteDesignCards = (
    <>
      {coreDesignCards}
      {fontCard}
    </>
  );

  const logoCards = (
    <>
          <Card className="shadow-sm border-0" data-testid="appearance-logo">
            <CardHeader>
              <CardTitle>Organization logo</CardTitle>
              <CardDescription>
                Shown in your public site&apos;s header and homepage. Upload one or generate it with AI.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <LogoEditor
                league={league}
                admin={admin}
                narrow={!mobilePreview}
                onSaved={refreshOrg}
                onDraftChange={setLogoDraft}
                externalSaveSignal={logoSaveSignal}
              />
              {/* Logo emphasis lives INSIDE the logo card (owner 2026-09-09).
                  Prominent = big logo beside the org name; the hero's right
                  rail (classic value props / premier featured card) drops to
                  a horizontal bottom row. Classic/premier only — and only
                  once a logo actually EXISTS (saved or pending draft; owner
                  2026-09-25: sizing a logo you don't have is noise). */}
              {template !== 'simple' && (league?.hasLogo || Boolean(logoDraft)) && (
                <div className="mt-5 border-t border-border pt-4" data-testid="appearance-logo-emphasis">
                  <p className="text-sm font-medium mb-1">Logo emphasis</p>
                  <p className="text-xs text-muted-foreground mb-3">How large the logo appears in your homepage hero.</p>
                  <div className={mobilePreview ? 'grid gap-3 sm:grid-cols-2' : 'grid gap-3 grid-cols-1'} role="radiogroup" aria-label="Logo emphasis">
                    {([
                      { key: 'standard', name: 'Standard', blurb: 'Small logo above the name (the current look).' },
                      { key: 'prominent', name: 'Prominent', blurb: template === 'premier'
                        ? 'Big logo beside the name; the featured competition card moves below the hero.'
                        : 'Big logo beside the name; the value-prop highlights move below the hero.' },
                    ] as const).map((opt) => (
                      <button
                        key={opt.key}
                        type="button"
                        role="radio"
                        aria-checked={heroLogo === opt.key}
                        onClick={() => setHeroLogo(opt.key)}
                        className={swatchBtn(heroLogo === opt.key)}
                      >
                        <span className="text-sm font-medium">{opt.name}</span>
                        <p className="text-xs text-muted-foreground">{opt.blurb}</p>
                      </button>
                    ))}
                  </div>
                  {!league?.hasLogo && (
                    <p className="mt-2 text-xs text-muted-foreground">
                      Upload or generate a logo above to see the effect. Without one the hero just shows your name.
                    </p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

    </>
  );

  const bannerCards = template === 'simple' ? (
            <>
              <Card className="shadow-sm border-0" data-testid="appearance-banner-editor">
                <CardHeader>
                  <CardTitle>Customize banner</CardTitle>
                  <CardDescription>
                    Build a banner from a template or upload your own image.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <BannerEditor
                    league={league}
                    admin={admin}
                    ctaLabel="Save banner"
                    compact
                    narrow={!mobilePreview}
                    onDraftChange={setBannerDraft}
                    onSaved={(savedOrg: AnyDoc) => { refreshOrg(savedOrg); toast.success('Banner saved'); }}
                    externalSaveSignal={bannerSaveSignal}
                    onSaveableChange={setBannerSaveable}
                  />
                </CardContent>
              </Card>
            </>
  ) : null;

  // Per-section header row for the tabbed layout: title + its OWN save.
  const sectionHeader = (title: string, button: React.ReactNode | null) => (
    <div className="flex items-center justify-between gap-3">
      <h2 className="text-base font-semibold">{title}</h2>
      {button}
    </div>
  );

  // The active tab's title + save, rendered INSIDE the sticky chrome so the
  // organizer can save from any scroll depth (owner 2026-09-25).
  const activeSectionHeader = activeSection === 'banner'
    ? sectionHeader('Banner', (
      <Button
        onClick={() => setBannerSaveSignal((n) => n + 1)}
        disabled={!bannerSaveable.canSave || bannerSaveable.busy}
        data-testid="appearance-save-banner"
      >
        {bannerSaveable.busy ? 'Saving…' : 'Save banner'}
      </Button>
    ))
    : activeSection === 'logo'
      ? sectionHeader('Logo', (
        <Button onClick={saveLogo} disabled={saving || !logoDirty} data-testid="appearance-save-logo">
          {saving ? 'Saving…' : 'Save logo'}
        </Button>
      ))
      : sectionHeader('Site design', (
        <Button onClick={saveSiteDesign} disabled={saving || !siteDirty || customInvalid} data-testid="appearance-save-site">
          {saving ? 'Saving…' : 'Save site design'}
        </Button>
      ));

  const controls = layout === 'tabbed' ? (
    <Tabs value={activeSection} onValueChange={(v: string) => setActiveSection(v as AppearanceSection)}>
      {/* Sticky chrome (owner 2026-09-25): tab bar + the active section's
          title/save stay visible while the cards scroll. top-[84px] clears
          the page's sticky header (measured 85px tall; the 1px overlap
          hides the seam). */}
      <div className="sticky top-[84px] z-20 -mx-1 px-1 pb-3 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 border-b border-border">
        <TabsList className="w-full" data-testid="appearance-tabs">
          <TabsTrigger value="site-design">Site design</TabsTrigger>
          <TabsTrigger value="logo">Logo</TabsTrigger>
          {template === 'simple' && <TabsTrigger value="banner">Banner</TabsTrigger>}
        </TabsList>
        <div className="mt-3">{activeSectionHeader}</div>
      </div>
      <TabsContent value="site-design" className="mt-4 space-y-6">
        {siteDesignCards}
      </TabsContent>
      <TabsContent value="logo" className="mt-4 space-y-6">
        {logoCards}
      </TabsContent>
      {template === 'simple' && (
        <TabsContent value="banner" className="mt-4 space-y-6">
          {bannerCards}
        </TabsContent>
      )}
    </Tabs>
  ) : (
    <>
      {sectionLabel('Site design')}
      {advancedCollapsed ? coreDesignCards : siteDesignCards}
      {advancedCollapsed ? (
        /* INT-0028: font + logo (incl. the AI generator) behind a closed
           disclosure. Radix unmounts the content while closed, so the
           LogoEditor is not in the DOM until the organizer opens it. */
        <Collapsible open={advancedOpen} onOpenChange={setAdvancedOpen} className="rounded-lg bg-muted">
          <CollapsibleTrigger asChild>
            <button
              type="button"
              className="w-full flex items-center justify-between p-4 text-left"
              aria-expanded={advancedOpen}
              data-tour="create-design-advanced"
              data-testid="appearance-advanced"
            >
              <span className="text-sm font-medium">Advanced: logo and font</span>
              <ChevronDown className={`h-4 w-4 transition-transform ${advancedOpen ? 'rotate-180' : ''}`} />
            </button>
          </CollapsibleTrigger>
          <CollapsibleContent className="px-4 pb-4 space-y-6">
            {fontCard}
            {sectionLabel('Logo')}
            {logoCards}
          </CollapsibleContent>
        </Collapsible>
      ) : (
        <>
          {sectionLabel('Logo')}
          {logoCards}
        </>
      )}
      {bannerCards ? (
        <>
          {sectionLabel('Banner')}
          {bannerCards}
        </>
      ) : null}
    </>
  );

  /** The browser-framed live preview — the page owns the wrapping column. */
  const preview = (
    <>

          <Card className="shadow-sm border-0">
            <CardHeader className="flex flex-row items-center justify-between space-y-0">
              <div>
                <CardTitle>Live preview</CardTitle>
                <CardDescription>Your real site with the current (unsaved) choices.</CardDescription>
              </div>
              <div className="flex gap-1">
                <Button
                  variant={mobilePreview ? 'ghost' : 'default'}
                  size="icon"
                  aria-label="Desktop preview"
                  aria-pressed={!mobilePreview}
                  onClick={() => setMobilePreview(false)}
                >
                  <Monitor className="h-4 w-4" />
                </Button>
                <Button
                  variant={mobilePreview ? 'default' : 'ghost'}
                  size="icon"
                  aria-label="Mobile preview"
                  aria-pressed={mobilePreview}
                  onClick={() => setMobilePreview(true)}
                >
                  <Smartphone className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {previewSrc ? (
                <SitePreviewFrame src={previewSrc} mobile={mobilePreview} />
              ) : (
                <ListSkeleton rows={3} />
              )}
            </CardContent>
          </Card>
        
    </>
  );

  return { controls, preview, save, saveSiteDesign, saveLogo, saving, isDirty, siteDirty, logoDirty, mobilePreview, setMobilePreview };
}
